import { Component, OnInit, AfterViewInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import moment from 'moment';
import * as registrationStrings from '../stringFiles/registration.json';
import { LoginService } from '../login/login.service';
import { FeeManagementService } from '../feesmanage/feesmanage.service';
import { FormValidator } from '../form.validator';
import { TransportService } from '../transport/transport.service';

declare var customToastr: any;
declare var google: any;

type TransportTab = 'BUS' | 'ROUTE' | 'STOP' | 'TERMS';
type BusFilter = 'all' | 'active' | 'inactive';
type TvModal =
  | 'addBus' | 'editBus' | 'viewBus' | 'deleteBus' | 'students'
  | 'addRoute' | 'editRoute' | 'deleteRoute' | 'assignStops'
  | 'addStop' | 'editStop' | 'deleteStop'
  | 'addTerm' | 'editTerm' | 'deleteTerm' | 'migrate';

// Same key the old transport component shipped with (kept here rather than
// moved to config so both screens stay in lock-step until the old one is retired).
const GOOGLE_MAPS_KEY = 'AIzaSyDHWle0ySVx-1H7fKACXpMERZ01g9gyEIM';
const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 };

export interface TvColumn {
  label: string;
  key: string;
  sortable?: boolean;
}

/**
 * Client-side search / sort / paging for one table. The old screen used
 * jQuery DataTables bound to the DOM; this keeps the same behaviour (search,
 * sortable headers, page size, export of the filtered rows) as plain state.
 */
export class TvTable {
  rows: any[] = [];
  filtered: any[] = [];
  pageRows: any[] = [];
  search = '';
  sortKey = '';
  sortDir: 'asc' | 'desc' = 'asc';
  page = 1;
  size = 10;
  pages = 1;
  readonly sizes = [10, 25, 50, 100];

  constructor(private searchKeys: string[], defaultSort = '') {
    this.sortKey = defaultSort;
  }

  setRows(rows: any[]): void {
    this.rows = rows || [];
    this.page = 1;
    this.refresh();
  }

  refresh(): void {
    const q = (this.search || '').trim().toLowerCase();
    let list = this.rows;
    if (q) {
      list = list.filter((r) => this.searchKeys.some((k) => String(r?.[k] ?? '').toLowerCase().includes(q)));
    }
    if (this.sortKey) {
      const k = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const av = a?.[k];
        const bv = b?.[k];
        const an = Number(av);
        const bn = Number(bv);
        if (av !== '' && bv !== '' && !isNaN(an) && !isNaN(bn)) {
          return (an - bn) * dir;
        }
        return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true, sensitivity: 'base' }) * dir;
      });
    }
    this.filtered = list;
    this.pages = Math.max(1, Math.ceil(list.length / this.size));
    this.page = Math.min(this.page, this.pages);
    this.pageRows = list.slice((this.page - 1) * this.size, this.page * this.size);
  }

  onSearch(): void {
    this.page = 1;
    this.refresh();
  }

  onSize(): void {
    this.page = 1;
    this.refresh();
  }

  sort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.refresh();
  }

  go(page: number): void {
    this.page = Math.min(Math.max(1, page), this.pages);
    this.refresh();
  }
}

@Component({
  selector: 'app-transport-v2',
  templateUrl: './transport-v2.component.html',
  // Same legacy theme list as FeesManageV2Component/DashboardV2Component: with
  // ViewEncapsulation.None each component's styleUrls are re-injected when it
  // mounts, which is what keeps #menubar's width rule and the header laid out
  // correctly on this page (the global bundle alone loses the cascade).
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css',
    './../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css',
    './../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css',
    './../../_crop/css/style.css',
    './../../_crop/css/style-example.css',
    './transport-v2.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [LoginService, TransportService, FeeManagementService, FormValidator],
})
export class TransportV2Component implements OnInit, AfterViewInit, OnDestroy {
  private strings: any = registrationStrings;

  getAuthToken = localStorage.getItem('usertoken');
  getSchoolId = localStorage.getItem('schoolinfo');
  loginMode = '';
  currType = 'INR';

  // ===== Academic year =====
  academicList: any[] = [];
  currentAcademicId: any = '';
  academicState = '';

  // ===== Page state =====
  activeTab: TransportTab = 'BUS';
  busFilter: BusFilter = 'all';
  modal: TvModal | null = null;
  saving = false;
  migrating = false;

  // ===== Counts =====
  activeBus: any = 0;
  inActiveBus: any = 0;
  totalVehicles: any = 0;

  // ===== Tables =====
  busTables: Record<BusFilter, TvTable> = {
    all: new TvTable(['BusName', 'staffName', 'BusNumber', 'busInChargeName', 'BusStatus'], 'BusName'),
    active: new TvTable(['BusName', 'staffName', 'BusNumber', 'busInChargeName', 'BusStatus'], 'BusName'),
    inactive: new TvTable(['BusName', 'staffName', 'BusNumber', 'busInChargeName', 'BusStatus'], 'BusName'),
  };
  busLoading = false;
  routeTable = new TvTable(['RouteName', 'startName', 'endName', 'TotalStop'], 'RouteName');
  stopTable = new TvTable(['BusStopName', 'fare'], 'BusStopName');
  termTable = new TvTable(['termName', 'termDueDate', 'termStartDate', 'termEndDate'], 'termName');
  studentTable = new TvTable(['studentName', 'parentName', 'mobileNumber', 'registrationId', 'class', 'RouteName', 'BusStopName'], 'studentName');
  studentsLoading = false;

  busColumns: TvColumn[] = [
    { label: 'bus', key: 'BusName', sortable: true },
    { label: 'driver', key: 'staffName', sortable: true },
    { label: 'busNumber', key: 'BusNumber', sortable: true },
    { label: 'busInCharge', key: 'busInChargeName', sortable: true },
    { label: 'tv2CapacityUsed', key: 'Capacity', sortable: true },
    { label: 'status', key: 'BusStatus', sortable: true },
    { label: 'manage', key: '' },
  ];
  routeColumns: TvColumn[] = [
    { label: 'routeName', key: 'RouteName', sortable: true },
    { label: 'startingPoint', key: 'startName', sortable: true },
    { label: 'endingPoint', key: 'endName', sortable: true },
    { label: 'totalStops', key: 'TotalStop', sortable: true },
    { label: 'tv2AssignStops', key: '' },
    { label: 'manage', key: '' },
  ];
  stopColumns: TvColumn[] = [
    { label: 'stopName', key: 'BusStopName', sortable: true },
    { label: 'tv2PerTermFee', key: 'fare', sortable: true },
    { label: 'tv2NoOfTerms', key: '' },
    { label: 'manage', key: '' },
  ];
  termColumns: TvColumn[] = [
    { label: 'termName', key: 'termName', sortable: true },
    { label: 'dueDate', key: 'termDueDate', sortable: true },
    { label: 'tv2TermStart', key: 'termStartDate', sortable: true },
    { label: 'tv2TermEnd', key: 'termEndDate', sortable: true },
    { label: 'manage', key: '' },
  ];
  studentColumns: TvColumn[] = [
    { label: 'name', key: 'studentName', sortable: true },
    { label: 'parent', key: 'parentName', sortable: true },
    { label: 'mobile', key: 'mobileNumber', sortable: true },
    { label: 'registrationNumber', key: 'registrationId', sortable: true },
    { label: 'class', key: 'class', sortable: true },
    { label: 'busNumber', key: 'BusNumber', sortable: true },
    { label: 'busName', key: 'BusName', sortable: true },
    { label: 'capacity', key: 'Capacity', sortable: true },
    { label: 'routeName', key: 'RouteName', sortable: true },
    { label: 'busStop', key: 'BusStopName', sortable: true },
  ];

  // ===== Lookups =====
  busRouteList: any[] = [];
  busStopList: any[] = [];
  driverList: any[] = [];
  termCount = 0;

  // ===== Bus forms =====
  addBusForm: any = this.emptyBus();
  busForm: any = {};
  busDetails: any = {};
  selectedBusData: any = null;
  selectedBusId: any = 0;
  selectedBusListType: BusFilter = 'all';
  busDetailLoading = false;

  // ===== Route forms =====
  addRouteForm = { RouteName: '', StartPoint: '', EndPoint: '', TotalStops: '' as any };
  routeForm: any = { RouteName: null, StartPoint: null, EndPoint: null, TotalStop: null };
  selectedRouteId: any = 0;
  selectedRoute: any = { TotalStop: 0, assignedStops: 0 };
  assignRows: { stopId: string; stopNumber: number; lockedStop: boolean; lockedNumber: boolean }[] = [];
  assignLoading = false;

  // ===== Stop forms =====
  addStopForm: any = { BusStopName: '', fare: '', Latitude: '', Longitude: '' };
  stopForm: any = { BusStopName: null, Busfare: null, Latitude: '', Longitude: '' };
  selectedStopId: any = 0;

  // ===== Term forms =====
  addTermForm: any = { termName: '', termDue: '', termStart: '', termEnd: '' };
  termForm: any = { termName: '', termDue: '', termStart: '', termEnd: '' };
  selectedTermId: any = null;

  // ===== Migration =====
  currentAcademicYear: any = { Year: null, isTransportMigrationStarted: null, pkAcademicId: null };
  previousAcademic: any = { Year: null, pkAcademicId: null };
  migrationStarted = false;

  private maps: { add?: any; edit?: any } = {};

  constructor(
    private router: Router,
    private loginService: LoginService,
    private transportService: TransportService,
    private feeService: FeeManagementService,
    private formValidator: FormValidator,
    private translate: TranslateService
  ) {}

  // ================= Lifecycle =================
  ngOnInit(): void {
    this.loginMode = localStorage.getItem('loginMode');
    this.currType = localStorage.getItem('currency') || 'INR';
    if (this.loginMode === 'STAFF') {
      this.router.navigate(['/exams']);
      return;
    }
    this.isValidToken();
  }

  private resizeListener = () => this.placeSlideIndicators();

  ngAfterViewInit(): void {
    this.placeSlideIndicators();
    window.addEventListener('resize', this.resizeListener);
    // Label widths change once the web font is ready.
    (document as any).fonts?.ready?.then(() => this.placeSlideIndicators());
  }

  ngOnDestroy(): void {
    this.maps = {};
    window.removeEventListener('resize', this.resizeListener);
  }

  // Moves each tab group's highlight under its active button (same approach
  // as the fees-v2 tab indicator, generalised to every .tv2-slider group).
  private placeSlideIndicators(): void {
    document.querySelectorAll<HTMLElement>('.tv2-slider').forEach((track) => {
      const indicator = track.querySelector<HTMLElement>('.tv2-slide-indicator');
      const active = track.querySelector<HTMLElement>('.tv2-tab.active, .tv2-subtab-active');
      if (!indicator) {
        return;
      }
      if (!active) {
        indicator.style.width = '0';
        return;
      }
      indicator.style.transform = `translateX(${active.offsetLeft}px)`;
      indicator.style.width = `${active.offsetWidth}px`;
    });
  }

  private isValidToken(): void {
    this.loginService.isValidToken(this.getAuthToken).subscribe({
      next: (data: any) => {
        if (data.errorcode == 10010) {
          this.router.navigate(['/suspended']);
        } else if (data.isValid == true) {
          this.loginService.isValidSchoolId(localStorage.getItem('schoolinfo')).subscribe({
            next: (isValidData: any) => {
              this.getSchoolId = localStorage.getItem('schoolinfo');
              if (isValidData.schoolExist != 1) {
                this.router.navigate(['/login']);
              } else {
                this.initializeAcademicData();
              }
            },
            error: () => {},
          });
        } else {
          this.router.navigate(['/login']);
        }
      },
      error: () => {},
    });
  }

  private initializeAcademicData(): void {
    this.feeService.fetchAcademicList().subscribe({
      next: (data: any) => {
        if (data.errorcode) {
          return;
        }
        const list = data.academicList;
        if (list && list.length > 0) {
          this.academicList = list;
          for (const ac of this.academicList) {
            ac.current = false;
            ac.notstarted = false;
            ac.completed = false;
            if (ac.InActive == 0) {
              ac.current = true;
              this.currentAcademicId = ac.pkAcademicId;
              this.academicState = 'RUNNING';
              localStorage.setItem('current_academic', this.currentAcademicId);
            } else if (ac.InActive == 1) {
              if (new Date(ac.EndDate) < new Date()) {
                ac.completed = true;
              } else {
                ac.notstarted = true;
              }
            }
          }
        }
        this.setTab(this.activeTab);
      },
      error: () => {},
    });
  }

  academicChanged(id: any): void {
    this.currentAcademicId = id;
    const ac = this.academicList.find((a) => a.pkAcademicId == id);
    if (ac) {
      this.academicState = ac.current ? 'RUNNING' : ac.completed ? 'COMPLETED' : 'NOT_STARTED';
    }
    localStorage.setItem('current_academic', this.currentAcademicId);
    this.loadCounts();
    this.loadBuses('all');
    this.loadBuses('active');
    this.loadBuses('inactive');
    this.loadRoutes();
    this.loadStops();
    this.loadDrivers();
    this.loadTerms();
  }

  goToOldTransport(): void {
    this.router.navigate(['/transport']);
  }

  // Mirrors getTransportCall(): every tab switch refreshes the shared lookups
  // plus whatever the destination tab itself shows.
  setTab(tab: TransportTab): void {
    this.activeTab = tab;
    setTimeout(() => this.placeSlideIndicators());
    this.loadMigrationInfo();
    this.loadCounts();
    this.loadDrivers();
    this.loadRoutes();
    this.loadStops();
    if (tab === 'TERMS' || tab === 'STOP') {
      this.loadTerms();
    }
    if (tab === 'BUS') {
      this.loadBuses(this.busFilter);
    }
  }

  // ================= Toast helpers =================
  private async toast(key: string, type: 'success' | 'error' | 'warning' = 'error', title?: string): Promise<void> {
    const msg = await firstValueFrom(this.translate.get(key));
    customToastr(msg, type, title);
  }

  // ================= Counts / buses =================
  loadCounts(): void {
    this.transportService.fetchTransportCount(localStorage.getItem('schoolinfo'), this.getAuthToken).subscribe({
      next: (data: any) => {
        this.activeBus = Number(data[0].ActiveBus).toLocaleString('en');
        this.inActiveBus = Number(data[0].InActiveBus).toLocaleString('en');
        this.totalVehicles = Number(data[0].TotalVehicles).toLocaleString('en');
      },
      error: () => {},
    });
  }

  setBusFilter(filter: BusFilter): void {
    this.busFilter = filter;
    setTimeout(() => this.placeSlideIndicators());
    this.loadBuses(filter);
  }

  loadBuses(kind: BusFilter): void {
    const school = localStorage.getItem('schoolinfo');
    const call =
      kind === 'all'
        ? this.transportService.fetchAllBuses(school, this.getAuthToken)
        : kind === 'active'
        ? this.transportService.fetchActiveBuses(school, this.getAuthToken)
        : this.transportService.fetchInActiveBuses(school, this.getAuthToken);
    this.busLoading = true;
    call.subscribe({
      next: (data: any) => {
        this.busLoading = false;
        this.busTables[kind].setRows(Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : []);
      },
      error: () => {
        this.busLoading = false;
      },
    });
  }

  private reloadBusLists(): void {
    this.loadBuses('all');
    this.loadBuses('active');
    this.loadBuses('inactive');
    this.loadCounts();
  }

  loadDrivers(): void {
    this.transportService.fetchDriverList(this.getAuthToken).subscribe({
      next: (data: any) => (this.driverList = data || []),
      error: () => (this.driverList = []),
    });
  }

  // ================= Modal plumbing =================
  openModal(name: TvModal): void {
    this.modal = name;
  }

  closeModal(): void {
    this.modal = null;
    this.saving = false;
  }

  // ================= Bus: add =================
  private emptyBus(): any {
    return {
      BusNumber: '', BusName: '', Capacity: '', fkStaffId: '', DeviceId: '', fkRouteId: '',
      BusStatus: '', InChargeId: null, current_academic: localStorage.getItem('current_academic'),
    };
  }

  openAddBus(): void {
    this.addBusForm = this.emptyBus();
    this.loadRoutes();
    this.loadDrivers();
    this.openModal('addBus');
  }

  async saveNewBus(): Promise<void> {
    const f = this.addBusForm;
    const vName = this.formValidator.validateBusInfoFields(f.BusName, 'Bus name');
    const vNumber = this.formValidator.validateBusInfoFields(f.BusNumber, 'Bus number');
    const vCapacity = this.formValidator.validateBusInfoFields(f.Capacity, 'Bus capacity');
    const vDevice = this.formValidator.validateBusInfoFields(f.DeviceId, 'Tracking device');
    const complete =
      !vNumber && !vName && !vCapacity && !vDevice &&
      this.formValidator.validateIsDataForString(f.fkStaffId) &&
      this.formValidator.validateIsDataForString(f.fkRouteId) &&
      this.formValidator.validateIsDataForString(f.BusStatus);
    if (!complete) {
      const firstError = vName || vNumber || vCapacity || vDevice;
      if (firstError) {
        customToastr(firstError, 'error');
      } else {
        await this.toast('pleaseFillAllTheFields');
      }
      return;
    }
    f.current_academic = localStorage.getItem('current_academic');
    this.saving = true;
    this.transportService.addNewBus(f, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('busIsAddedSuccessfully', 'success');
          this.closeModal();
          this.reloadBusLists();
        } else if (data.status == false) {
          await this.toast('busIsAlreadyExist');
        } else {
          await this.toast('busIsNotAddedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  // ================= Bus: view / edit / delete =================
  busDetail(bus: any, listType: BusFilter, modal: TvModal): void {
    this.selectedBusData = bus;
    this.selectedBusListType = listType;
    this.selectedBusId = bus.pkBusId;
    this.busDetails = {};
    this.busForm = {};
    this.busDetailLoading = true;
    this.openModal(modal);
    if (modal === 'deleteBus') {
      this.busDetailLoading = false;
      return;
    }
    this.transportService.fetchBusesDetail(this.getAuthToken, bus.pkBusId).subscribe({
      next: (data: any) => {
        this.busDetailLoading = false;
        this.busDetails = data[0] || {};
        this.busForm = JSON.parse(JSON.stringify(this.busDetails));
        this.loadRoutes();
      },
      error: () => (this.busDetailLoading = false),
    });
  }

  remainingSeats(): number {
    return (parseInt(this.selectedBusData?.Capacity, 10) || 0) - (parseInt(this.selectedBusData?.assignedStudents, 10) || 0);
  }

  async saveBusEdit(): Promise<void> {
    const f = this.busForm;
    const ok =
      this.formValidator.validateIsDataForString(f.BusName) &&
      this.formValidator.validateIsDataForString(f.Capacity) &&
      this.formValidator.validateIsDataForString(f.fkStaffId) &&
      this.formValidator.validateIsDataForString(f.fkRouteId) &&
      this.formValidator.validateIsDataForString(f.BusStatus);
    if (!ok) {
      await this.toast('pleaseFillAllFields');
      return;
    }
    const assigned = this.selectedBusData.assignedStudents;
    if (!(assigned <= f.Capacity)) {
      const t = await firstValueFrom(this.translate.get(['notAllowedToChangeBusCapacityTo', 'studentsAreAssignedToThisBus', 'updateBusDetails', 'already']));
      customToastr(`${t.notAllowedToChangeBusCapacityTo} <b>${f.Capacity}</b>, ${t.already} <b>${assigned}</b> ${t.studentsAreAssignedToThisBus}`, 'error', t.updateBusDetails);
      return;
    }
    if (this.selectedBusData.pkRouteId != f.fkRouteId && assigned > '0') {
      const t = await firstValueFrom(this.translate.get(['notAllowedToChangeBusRouteAlready', 'studentsAreAssignedToThisBus', 'updateBusDetails']));
      customToastr(`${t.notAllowedToChangeBusRouteAlready} <b>${assigned}</b> ${t.studentsAreAssignedToThisBus}`, 'error', t.updateBusDetails);
      return;
    }
    this.saving = true;
    this.transportService.updateBus(f, this.selectedBusId, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('busUpdatedSuccessfully', 'success');
          this.closeModal();
          this.loadBuses('all');
          this.loadCounts();
          if (this.selectedBusListType === 'active') {
            this.loadBuses('active');
          } else if (this.selectedBusListType === 'inactive') {
            this.loadBuses('inactive');
          }
        } else {
          await this.toast('busNotUpdatedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  // The "all" list hides delete while students are assigned; the active /
  // inactive lists always showed it (the server still refuses and says why).
  canDeleteBus(bus: any, listType: BusFilter): boolean {
    return listType !== 'all' || bus.assignedStudents <= 0;
  }

  deleteBus(): void {
    this.saving = true;
    this.transportService.deleteBusDetail(this.getAuthToken, this.selectedBusId).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('busDeleted', 'success');
          this.closeModal();
          this.reloadBusLists();
        } else if (data.message) {
          this.closeModal();
          const title = await firstValueFrom(this.translate.get('deleteBus'));
          customToastr(data.message, 'error', title);
        } else {
          await this.toast('busNotDeletedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  openStudents(bus: any): void {
    this.selectedBusData = bus;
    this.studentsLoading = true;
    this.studentTable.setRows([]);
    this.studentTable.search = '';
    this.openModal('students');
    this.transportService.fetchStudentDetailsForOptedBus(bus.pkBusId, localStorage.getItem('schoolinfo'), this.getAuthToken).subscribe({
      next: (data: any) => {
        this.studentsLoading = false;
        this.studentTable.setRows(Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : []);
      },
      error: () => (this.studentsLoading = false),
    });
  }

  // ================= Routes =================
  loadRoutes(): void {
    this.transportService.fetchBusesRoute(this.getAuthToken).subscribe({
      next: (data: any) => {
        this.busRouteList = Array.isArray(data) ? data : [];
        this.routeTable.setRows([...this.busRouteList]);
      },
      error: () => {},
    });
  }

  async showRouteWarning(route: any): Promise<void> {
    if (route.assignedStops < route.TotalStop) {
      const t = await firstValueFrom(this.translate.get(['pleaseAssignAll', 'stopsTo', 'rightNowOnly', 'stops', 'stop', 'assigned']));
      customToastr(
        `${t['pleaseAssignAll']} <b>${route.TotalStop}</b> ${t['stopsTo']} <b>${route.RouteName}</b>${t['rightNowOnly']} <b>${route.assignedStops}</b> ${route.assignedStops > 1 ? t['stops'] : t['stop']}${t['assigned']}`,
        'warning'
      );
    }
  }

  openAddRoute(): void {
    this.addRouteForm = { RouteName: '', StartPoint: '', EndPoint: '', TotalStops: '' };
    this.openModal('addRoute');
  }

  async saveNewRoute(): Promise<void> {
    const f = this.addRouteForm;
    if (!f.RouteName || !f.StartPoint || !f.EndPoint || !f.TotalStops) {
      await this.toast('pleaseFillAllTheFields');
      return;
    }
    this.saving = true;
    this.transportService.insertBusRoute(f.RouteName, f.StartPoint, f.EndPoint, f.TotalStops, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('routeIsAddedSuccessfully', 'success');
          this.closeModal();
          this.loadRoutes();
        } else {
          await this.toast('routeIsNotAddedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  routeDetail(route: any, modal: TvModal): void {
    this.selectedRouteId = route.pkRouteId;
    this.selectedRoute = route;
    this.openModal(modal);
    if (modal === 'editRoute') {
      this.routeForm = { RouteName: null, StartPoint: null, EndPoint: null, TotalStop: null };
      this.transportService.fetchRouteDetail(this.getAuthToken, route.pkRouteId).subscribe({
        next: (data: any) => (this.routeForm = data[0] || this.routeForm),
        error: () => {},
      });
    }
  }

  async saveRouteEdit(): Promise<void> {
    const f = this.routeForm;
    if (!f.RouteName || !f.StartPoint || !f.EndPoint || !f.TotalStop) {
      await this.toast('pleaseFillAllFields');
      return;
    }
    if (!(parseInt(f.TotalStop, 10) >= this.selectedRoute.assignedStops)) {
      const t = await firstValueFrom(this.translate.get(['youCannotReduceNumberOfStopsLessThanTo', 'already', 'stopsAreAssignedToThisRoute']));
      customToastr(`${t['youCannotReduceNumberOfStopsLessThanTo']} <b>${this.selectedRoute.assignedStops}</b>, ${t['already']} ${this.selectedRoute.assignedStops}${t['stopsAreAssignedToThisRoute']}`, 'error');
      return;
    }
    this.saving = true;
    this.transportService.updateBusRoute(f.RouteName, f.StartPoint, f.EndPoint, f.TotalStop, this.selectedRouteId, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('routeIsUpdatedSuccessfully', 'success');
          this.closeModal();
          this.loadRoutes();
        } else {
          await this.toast('routeIsNotUpdatedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  deleteRoute(): void {
    this.saving = true;
    this.transportService.deleteRouteDetail(this.getAuthToken, this.selectedRouteId).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('routeDeleted', 'success');
          this.closeModal();
          this.loadRoutes();
        } else if (data.status == false) {
          await this.toast('routeIsUsedInBusCannotBeDeleted');
          this.closeModal();
        } else {
          await this.toast('routeNotDeletedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  // ---- Assign stops to a route ----
  get remainingStopsToAssign(): number {
    return (parseInt(this.selectedRoute?.TotalStop, 10) || 0) - this.assignRows.length;
  }

  openAssignStops(route: any): void {
    this.selectedRouteId = route.pkRouteId;
    this.selectedRoute = route;
    this.assignRows = [];
    this.assignLoading = true;
    this.loadStops();
    this.openModal('assignStops');
    this.transportService.fetchStopForRoute(this.getAuthToken, route.pkRouteId).subscribe({
      next: (data: any) => {
        this.assignLoading = false;
        const existing: any[] = Array.isArray(data) ? data : [];
        if (existing.length > 0) {
          this.assignRows = existing.map((s) => ({
            stopId: String(s.pkBusStopId),
            stopNumber: s.StopNumber,
            lockedStop: true,
            lockedNumber: true,
          }));
        } else {
          // Nothing assigned yet: the first row starts at the route's starting point.
          this.assignRows = [{ stopId: String(route.StartPoint ?? ''), stopNumber: 1, lockedStop: false, lockedNumber: true }];
        }
      },
      error: () => (this.assignLoading = false),
    });
  }

  addAssignRow(): void {
    if (this.remainingStopsToAssign <= 0) {
      return;
    }
    const isLast = this.remainingStopsToAssign === 1;
    this.assignRows.push({
      stopId: isLast ? String(this.selectedRoute.EndPoint ?? '') : '',
      stopNumber: this.assignRows.length + 1,
      lockedStop: false,
      lockedNumber: true,
    });
  }

  get hasNewAssignRows(): boolean {
    return this.assignRows.some((r) => !r.lockedStop);
  }

  async saveAssignedStops(): Promise<void> {
    const fresh = this.assignRows.filter((r) => !r.lockedStop);
    if (fresh.some((r) => !r.stopId || !r.stopNumber)) {
      await this.toast('provideProperStopDetails');
      return;
    }
    if (fresh.length === 0) {
      await this.toast('provideAddAStopToSave');
      return;
    }
    const reqObj = fresh.map((r) => ({ stopId: r.stopId, stopNumber: r.stopNumber }));
    this.saving = true;
    this.transportService.assignStopForRoute(reqObj, this.selectedRouteId, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          this.loadRoutes();
          await this.toast('stopIsAssignToRouteSuccessfully', 'success');
          this.closeModal();
        } else if (data.status == false) {
          await this.toast('stopIsAlreadyExistInThisRoute');
        } else {
          await this.toast('stopIsNotAssignToRoutePleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  // ================= Stops =================
  loadStops(): void {
    this.transportService.fetchBusStop(this.currentAcademicId || localStorage.getItem('current_academic'), this.getAuthToken).subscribe({
      next: (data: any) => {
        this.busStopList = Array.isArray(data) ? data : [];
        this.stopTable.setRows([...this.busStopList]);
      },
      error: () => {},
    });
  }

  openAddStop(): void {
    this.addStopForm = { BusStopName: '', fare: '', Latitude: '', Longitude: '' };
    this.openModal('addStop');
    this.mountMap('add');
  }

  async saveNewStop(): Promise<void> {
    const f = this.addStopForm;
    if (!f.BusStopName || !f.fare) {
      await this.toast('pleaseFillAllTheFields');
      return;
    }
    this.saving = true;
    this.transportService.addBusStop(f.BusStopName, f.fare, f.Latitude, f.Longitude, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('stopsIsAddedSuccessfully', 'success');
          this.closeModal();
          this.loadStops();
        } else {
          customToastr(!data.status && data.message ? data.message : await firstValueFrom(this.translate.get('stopsIsNotAddedPleaseTryAgain')), 'error');
        }
      },
      error: () => (this.saving = false),
    });
  }

  openEditStop(stop: any): void {
    this.selectedStopId = stop.PkBusStopId;
    this.stopForm = { BusStopName: null, Busfare: null, Latitude: '', Longitude: '' };
    this.openModal('editStop');
    this.transportService.fetchStopDetail(this.getAuthToken, stop.PkBusStopId).subscribe({
      next: (data: any) => {
        this.stopForm = data[0] || this.stopForm;
        this.mountMap('edit');
      },
      error: () => {},
    });
  }

  async saveStopEdit(): Promise<void> {
    const f = this.stopForm;
    if (!f.BusStopName || !f.Busfare) {
      await this.toast('pleaseFillAllTheFields');
      return;
    }
    this.saving = true;
    this.transportService.updateBusStop(f.BusStopName, f.Busfare, f.Latitude, f.Longitude, this.selectedStopId, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('stopsIsUpdatedSuccessfully', 'success');
          this.closeModal();
          this.loadStops();
        } else if (data.message == 'FEES_PAID') {
          await this.toast('stopsIsNotUpdatedSomeStudentsAreAlreadyPaidFees');
        } else if (data.message == 'AlreadyExist') {
          await this.toast('stopAlreadyExistWithSameNamePleaseChangeIt');
        } else {
          await this.toast('stopsIsNotUpdatedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  openDeleteStop(stop: any): void {
    this.selectedStopId = stop.PkBusStopId;
    this.openModal('deleteStop');
  }

  deleteStop(): void {
    this.saving = true;
    this.transportService.deleteStopDetail(this.getAuthToken, this.selectedStopId).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.status == true) {
          await this.toast('stopDeleted', 'success');
          this.closeModal();
          this.loadStops();
        } else if (data.status == false) {
          await this.toast('stopIsUsedInRouteCannotBeDelete');
          this.closeModal();
        } else {
          await this.toast('stopNotDeletedPleaseTryAgain');
        }
      },
      error: () => (this.saving = false),
    });
  }

  // ---- Google map pickers (add / edit stop) ----
  private loadGoogleMaps(): Promise<void> {
    return new Promise((resolve) => {
      if ((window as any).google?.maps?.places) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[data-tv2-gmaps]') as HTMLScriptElement;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-tv2-gmaps', '1');
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }

  private async mountMap(kind: 'add' | 'edit'): Promise<void> {
    await this.loadGoogleMaps();
    // Let the modal render its map container first.
    setTimeout(() => {
      const mapEl = document.getElementById(kind === 'add' ? 'tv2AddStopMap' : 'tv2EditStopMap');
      const input = document.getElementById(kind === 'add' ? 'tv2AddStopSearch' : 'tv2EditStopSearch') as HTMLInputElement;
      if (!mapEl || !input) {
        return;
      }
      const form = kind === 'add' ? this.addStopForm : this.stopForm;
      const setCoords = (lat: number, lng: number, digits = 6) => {
        form.Latitude = lat.toFixed(digits);
        form.Longitude = lng.toFixed(digits);
      };
      const hasCoords = form.Latitude && form.Longitude && !isNaN(parseFloat(form.Latitude));
      const center = hasCoords ? { lat: parseFloat(form.Latitude), lng: parseFloat(form.Longitude) } : DEFAULT_MAP_CENTER;

      const map = new google.maps.Map(mapEl, {
        center,
        zoom: 15,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      });
      const marker = new google.maps.Marker({ map, draggable: true, position: center, animation: google.maps.Animation.DROP });
      this.maps[kind] = map;

      map.addListener('click', (event: any) => {
        marker.setPosition(event.latLng);
        setCoords(event.latLng.lat(), event.latLng.lng());
      });
      marker.addListener('dragend', () => {
        const p = marker.getPosition();
        setCoords(p.lat(), p.lng());
      });

      const autocomplete = new google.maps.places.Autocomplete(input, {
        fields: ['formatted_address', 'geometry', 'name', 'place_id'],
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'IN' },
        strictBounds: false,
      });
      autocomplete.bindTo('bounds', map);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          return;
        }
        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(17);
        }
        marker.setPosition(place.geometry.location);
        marker.setAnimation(google.maps.Animation.DROP);
        form.Latitude = place.geometry.location.lat().toString();
        form.Longitude = place.geometry.location.lng().toString();
        input.value = place.formatted_address || place.name || '';
      });

      // The suggestions dropdown must sit above the modal backdrop.
      setTimeout(() => {
        Array.from(document.getElementsByClassName('pac-container')).forEach((c: any) => {
          c.style.zIndex = '10000';
          if (c.parentElement !== document.body) {
            document.body.appendChild(c);
          }
        });
      }, 300);
    }, 300);
  }

  // ================= Terms =================
  loadTerms(): void {
    this.transportService.getStopTerms(this.getAuthToken).subscribe({
      next: (data: any) => {
        if (data.success) {
          const list = JSON.parse(JSON.stringify(data.data));
          this.termCount = list.length;
          this.termTable.setRows(list);
        }
      },
      error: () => {},
    });
  }

  openAddTerm(): void {
    this.addTermForm = { termName: '', termDue: '', termStart: '', termEnd: '' };
    this.openModal('addTerm');
  }

  async saveNewTerm(): Promise<void> {
    const f = this.addTermForm;
    if (!f.termName || !f.termDue) {
      await this.toast('pleaseFillAllTheFields');
      return;
    }
    const termObj = {
      termName: f.termName,
      termDue: f.termDue,
      termStartDate: f.termStart !== '' ? f.termStart : null,
      termEndDate: f.termEnd !== '' ? f.termEnd : null,
    };
    this.saving = true;
    this.transportService.addStopTerm(termObj, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.success) {
          await this.toast('termAddedSuccessfully', 'success');
          this.closeModal();
          this.loadTerms();
        } else {
          customToastr(data.errmessage || 'Failed to add term', 'error');
        }
      },
      error: () => (this.saving = false),
    });
  }

  openEditTerm(term: any): void {
    this.selectedTermId = term.pkBusStopTermId;
    this.termForm = { termName: '', termDue: '', termStart: '', termEnd: '' };
    this.openModal('editTerm');
    this.transportService.getTermInfo(this.selectedTermId, this.getAuthToken).subscribe({
      next: (data: any) => {
        if (data.success) {
          const d = data.data;
          this.termForm = {
            termName: d.termName,
            termDue: d.termDueDate ? moment(d.termDueDate).format('YYYY-MM-DD') : '',
            termStart: d.termStartDate ? moment(d.termStartDate).format('YYYY-MM-DD') : '',
            termEnd: d.termEndDate ? moment(d.termEndDate).format('YYYY-MM-DD') : '',
          };
        } else {
          customToastr(data.errmessage, 'error');
        }
      },
      error: () => {},
    });
  }

  saveTermEdit(): void {
    const f = this.termForm;
    if (!f.termName || !f.termDue || !f.termStart || !f.termEnd) {
      return;
    }
    const termObj = {
      pkBusStopTermId: this.selectedTermId,
      termName: f.termName,
      termDueDate: f.termDue,
      termStartDate: f.termStart,
      termEndDate: f.termEnd,
    };
    this.saving = true;
    this.transportService.editStopTerms(termObj, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.success) {
          await this.toast('termEditedSuccessfully', 'success');
        } else {
          customToastr(data.errmessage, 'error');
        }
        this.closeModal();
        this.loadTerms();
      },
      error: () => (this.saving = false),
    });
  }

  openDeleteTerm(term: any): void {
    this.selectedTermId = term.pkBusStopTermId;
    this.openModal('deleteTerm');
  }

  deleteTerm(): void {
    this.saving = true;
    this.transportService.deleteStopTerms(this.selectedTermId, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.saving = false;
        if (data.success) {
          await this.toast('termDeletedSuccessfully', 'success');
          this.loadTerms();
        } else {
          customToastr(data.errmessage, 'error');
        }
        this.closeModal();
      },
      error: () => (this.saving = false),
    });
  }

  // ================= Migration =================
  loadMigrationInfo(): void {
    this.migrationStarted = false;
    this.transportService.currentAndPreviousAcademicService(this.getAuthToken).subscribe({
      next: (data: any) => {
        if (data.errorcode) {
          return;
        }
        this.currentAcademicYear = data.academic;
        this.previousAcademic = data.previousAcademic;
        this.migrationStarted = this.currentAcademicYear.isTransportMigrationStarted == 'YES';
      },
      error: () => {},
    });
  }

  migrateTransport(): void {
    this.closeModal();
    this.migrating = true;
    const academic = {
      oldAcademicId: this.previousAcademic.pkAcademicId,
      currentAcademicId: this.currentAcademicYear.pkAcademicId,
    };
    this.transportService.migrateTransportService(academic, this.getAuthToken).subscribe({
      next: async (data: any) => {
        this.migrating = false;
        if (data.success) {
          await this.toast('transportMigrationSuccessfullyCompleted', 'success');
          this.setTab(this.activeTab);
        } else if (data.errmessage == this.strings.migrationNotAllowed) {
          await this.toast('transportIsAlredayConfiguredForTheSelectedAcademicYear');
        } else {
          await this.toast('transportMigrationNotCompletedPleaseTryAgain');
        }
      },
      error: async () => {
        this.migrating = false;
        await this.toast('internalServerError');
      },
    });
  }

  // ================= Export (filtered rows of a table) =================
  async exportTable(table: TvTable, columns: TvColumn[], fileKey: string, format: 'xlsx' | 'csv'): Promise<void> {
    const cols = columns.filter((c) => c.key);
    const labels = await firstValueFrom(this.translate.get(cols.map((c) => c.label)));
    const title = await firstValueFrom(this.translate.get(fileKey));
    const rows = table.filtered.map((r) => {
      const out: any = {};
      cols.forEach((c) => (out[labels[c.label]] = r[c.key] ?? ''));
      return out;
    });
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, String(title).slice(0, 31));
    if (format === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(sheet);
      FileSaver.saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${title}.csv`);
    } else {
      const out = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
      FileSaver.saveAs(new Blob([out], { type: 'application/octet-stream' }), `${title}.xlsx`);
    }
  }

  statusLabelKey(status: string): string {
    return status === 'RUNNING' ? 'active' : 'inactive';
  }
}
